<!--
  OrderPlacedCard — the beat between placing an order and live tracking.

  Placing an order used to jump straight from a masked button to a new route
  that opened under a full-screen grey loading mask. Four unrelated visual
  states in about a second, and the one thing worth seeing — the order number —
  never appeared at all.

  This holds the panel for a moment instead: the check draws, the order number
  lands, and a progress line runs down while tracking is opened.

  The line is DETERMINATE, unlike the one on ActionButton, and that difference
  is deliberate. Here the wait is a delay this component itself imposes, so its
  duration is known and the bar is telling the truth. The button's waits are
  server round-trips of unknown length, so that one stays indeterminate. A
  progress bar should only be precise about something it actually knows.

  A Dedicated booking (`bookedFor`, the pickup as "Tue 4 Nov, 08:00") also
  says when it is booked for and that Xobo assigns the driver.

  It emits `done` when the hold elapses; the parent navigates. Nothing here
  routes, so the hold cannot strand the customer if navigation is refused.

  NOTE ON COLOUR: this renders directly in `.new-request-panel-content`, whose
  panel is `background: #ffffff`. The first version used white text, having
  been previewed against the navy panel header — the same mistake made on
  ActionButton in the same pass. Colours are for a light surface.
-->
<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

const props = withDefaults(defineProps<{
  orderNumber: string
  /** How long to hold before emitting `done`, in ms. */
  holdMs?: number
  /** Something the customer should know about the placed order. */
  notice?: string | null
  /** A Dedicated booking's pickup, as "Tue 4 Nov, 08:00". */
  bookedFor?: string | null
}>(), { holdMs: 1500, notice: null, bookedFor: null })

const emit = defineEmits<{ (e: 'done'): void }>()

const filled = ref(false)
let timer: number | null = null

onMounted(() => {
  // Next frame, so the transition has a start value to animate from rather
  // than snapping straight to 100%.
  requestAnimationFrame(() => { filled.value = true })
  timer = window.setTimeout(() => emit('done'), props.holdMs)
})

onBeforeUnmount(() => { if (timer !== null) window.clearTimeout(timer) })
</script>

<template>
  <div class="opc" role="status" aria-live="polite">
    <span class="opc__badge" aria-hidden="true">
      <svg viewBox="0 0 28 28" class="opc__tick">
        <circle cx="14" cy="14" r="12" class="opc__ring" />
        <path d="M8 14.5 L12.5 19 L20 10" class="opc__check" />
      </svg>
    </span>

    <h3 class="opc__title">Order placed</h3>
    <p class="opc__number">{{ orderNumber }}</p>
    <p v-if="bookedFor" class="opc__booked">Booked for {{ bookedFor }}. Xobo will assign your driver before pickup.</p>
    <p v-if="notice" class="opc__notice">{{ notice }}</p>
    <p class="opc__hint">Opening live tracking…</p>

    <div class="opc__bar" aria-hidden="true">
      <span
        class="opc__bar-fill"
        :class="{ 'is-filled': filled }"
        :style="{ transitionDuration: holdMs + 'ms' }"
      ></span>
    </div>
  </div>
</template>

<style scoped>
.opc {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 26px 20px 22px;
  font-family: 'DM Sans', system-ui, sans-serif;
  animation: opc-in 260ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

.opc__badge {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(22, 128, 60, 0.12);
  margin-bottom: 14px;
}
.opc__tick { width: 30px; height: 30px; fill: none; }
.opc__ring {
  stroke: rgba(22, 128, 60, 0.35);
  stroke-width: 1.6;
}
.opc__check {
  stroke: #16803c;
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 26;
  stroke-dashoffset: 26;
  animation: opc-draw 380ms 120ms ease-out forwards;
}

.opc__title {
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 700;
  color: #12284a;
  letter-spacing: 0.01em;
}

.opc__number {
  margin: 0 0 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #12284a;
  /* The one fact worth taking away from this screen, so it gets the emphasis
     the old flow never gave it. */
  padding: 5px 12px;
  border-radius: 7px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.opc__booked {
  margin: 0 0 10px;
  max-width: 300px;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #12284a;
}

.opc__hint {
  margin: 0 0 14px;
  font-size: 0.78rem;
  color: #64748b;
}

/* Same amber note as the booking panel's quote-limit warning. */
.opc__notice {
  margin: 4px 0 10px;
  padding: 9px 11px;
  border-radius: 8px;
  background: #fff8ed;
  border: 1px solid #fcd9a4;
  color: #8a5200;
  font-size: 0.78rem;
  line-height: 1.5;
}

.opc__bar {
  width: 120px;
  height: 3px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}
.opc__bar-fill {
  display: block;
  height: 100%;
  width: 100%;
  border-radius: inherit;
  background: #16803c;
  transform: scaleX(0);
  transform-origin: left center;
  transition-property: transform;
  transition-timing-function: linear;
}
.opc__bar-fill.is-filled { transform: scaleX(1); }

@keyframes opc-in {
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: none; }
}
@keyframes opc-draw { to { stroke-dashoffset: 0; } }

@media (prefers-reduced-motion: reduce) {
  .opc { animation: none; }
  .opc__check { animation: none; stroke-dashoffset: 0; }
  /* Keep the bar, drop the sweep — it still marks time without motion. */
  .opc__bar-fill { transition: none; transform: scaleX(1); opacity: 0.6; }
}
</style>
